import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Plus, X, Trash2, Edit3, Clock, BookOpen, Users, AlertCircle, Circle, Loader2 } from 'lucide-react';
import { api } from '../lib/api';

type EventType = 'lesson' | 'meeting' | 'deadline' | 'other';

interface CalendarEvent {
  id: string;
  title: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:MM
  type: EventType;
  description?: string;
}

const eventColors: Record<EventType, string> = {
  lesson: 'bg-cobalt-soft text-cobalt border-cobalt/20',
  meeting: 'bg-sage-soft text-sage-deep border-sage/20',
  deadline: 'bg-coral-soft text-coral border-coral/20',
  other: 'bg-sun-soft text-sun-deep border-sun/20',
};

const eventIcons = { lesson: BookOpen, meeting: Users, deadline: AlertCircle, other: Circle };
const eventLabels: Record<EventType, string> = {
  lesson: 'Unterricht', meeting: 'Besprechung', deadline: 'Deadline', other: 'Sonstiges',
};

const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

const pad = (n: number) => String(n).padStart(2, '0');
const dateStrOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Backend-Event (startTime/endTime/eventType) → UI-Event (date/time/type)
function fromApi(ev: any): CalendarEvent {
  const d = new Date(ev.startTime);
  return {
    id: ev.id,
    title: ev.title,
    date: dateStrOf(d),
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
    type: (['lesson', 'meeting', 'deadline', 'other'].includes(ev.eventType) ? ev.eventType : 'other') as EventType,
    description: ev.description || '',
  };
}

// UI-Felder → Backend-Payload (startTime/endTime als ISO, +1h Dauer)
function toApi(e: { title: string; date: string; time: string; type: EventType; description?: string }) {
  const start = new Date(`${e.date}T${e.time || '00:00'}`);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return {
    title: e.title,
    description: e.description || undefined,
    startTime: start.toISOString(),
    endTime: end.toISOString(),
    eventType: e.type,
  };
}

export default function CalendarView() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(() => dateStrOf(new Date()));
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [form, setForm] = useState({ title: '', time: '09:00', type: 'lesson' as EventType, description: '' });

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDayOfWeek = (firstDay.getDay() + 6) % 7;

  const days: (number | null)[] = [];
  for (let i = 0; i < startDayOfWeek; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const formatDate = (day: number) => `${year}-${pad(month + 1)}-${pad(day)}`;
  const dayEvents = events.filter((e) => e.date === selectedDate).sort((a, b) => a.time.localeCompare(b.time));
  const getEventCount = (dateStr: string) => events.filter((e) => e.date === dateStr).length;

  const loadEvents = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await api.calendar.list();
      setEvents(data.map(fromApi));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToToday = () => { const t = new Date(); setCurrentDate(t); setSelectedDate(dateStrOf(t)); };

  const closeDialog = () => {
    setShowAddDialog(false);
    setEditingEvent(null);
    setForm({ title: '', time: '09:00', type: 'lesson', description: '' });
  };

  const handleSave = async () => {
    if (!form.title.trim() || isSaving) return;
    setIsSaving(true);
    setError(null);
    try {
      const payload = toApi({ ...form, date: selectedDate });
      if (editingEvent) {
        await api.calendar.update(editingEvent.id, payload);
      } else {
        await api.calendar.create(payload);
      }
      await loadEvents();
      closeDialog();
    } catch (err: any) {
      setError('Speichern fehlgeschlagen: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.calendar.delete(id);
      setEvents((prev) => prev.filter((e) => e.id !== id));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const startEdit = (event: CalendarEvent) => {
    setEditingEvent(event);
    setSelectedDate(event.date);
    setForm({ title: event.title, time: event.time, type: event.type, description: event.description || '' });
    setShowAddDialog(true);
  };

  // ==================== ADD/EDIT DIALOG ====================
  if (showAddDialog) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="sl-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="sl-h2">{editingEvent ? 'Termin bearbeiten' : 'Neuer Termin'}</h2>
            <button onClick={closeDialog} className="p-2 rounded-lg hover:bg-paper-deep transition-colors">
              <X className="w-5 h-5 text-ink-mute" />
            </button>
          </div>

          {error && <p className="text-coral text-sm mb-4">{error}</p>}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">Titel *</label>
              <input type="text" placeholder="z.B. Mathe 5a" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })} className="sl-input" autoFocus />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ink-soft mb-1">Datum</label>
                <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="sl-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-soft mb-1">Uhrzeit</label>
                <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="sl-input" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">Typ</label>
              <div className="flex gap-2 flex-wrap">
                {(Object.keys(eventLabels) as EventType[]).map((type) => (
                  <button key={type} onClick={() => setForm({ ...form, type })}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${form.type === type ? eventColors[type] : 'bg-paper-deep text-ink-soft hover:bg-paper'}`}>
                    {eventLabels[type]}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">Beschreibung (optional)</label>
              <textarea placeholder="Notizen zum Termin..." value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="sl-input min-h-[80px] py-3 resize-none" />
            </div>

            <div className="flex gap-3 justify-end pt-2">
              <button onClick={closeDialog} className="sl-button sl-button-outline">Abbrechen</button>
              <button onClick={handleSave} disabled={!form.title.trim() || isSaving} className="sl-button sl-button-primary disabled:opacity-50 gap-2">
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingEvent ? 'Speichern' : 'Erstellen'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== LOADING ====================
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-cobalt animate-spin" />
      </div>
    );
  }

  // ==================== MAIN VIEW ====================
  return (
    <div className="max-w-5xl mx-auto">
      {error && <div className="sl-card p-3 mb-4 border-coral bg-coral-soft text-coral text-sm">{error}</div>}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Kalender */}
        <div className="lg:col-span-2 sl-card p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-paper-deep transition-colors"><ChevronLeft className="w-5 h-5" /></button>
              <h2 className="sl-h2">{monthNames[month]} {year}</h2>
              <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-paper-deep transition-colors"><ChevronRight className="w-5 h-5" /></button>
            </div>
            <button onClick={goToToday} className="sl-button sl-button-outline text-sm px-3 py-1.5 h-auto">Heute</button>
          </div>

          <div className="grid grid-cols-7 gap-1">
            {weekDays.map((day) => (
              <div key={day} className="text-center text-xs font-semibold text-ink-mute py-2">{day}</div>
            ))}
            {days.map((day, i) => {
              if (!day) return <div key={i} className="aspect-square" />;
              const dateStr = formatDate(day);
              const isSelected = dateStr === selectedDate;
              const isToday = dateStr === dateStrOf(new Date());
              const eventCount = getEventCount(dateStr);
              return (
                <button key={day} onClick={() => setSelectedDate(dateStr)}
                  className={`aspect-square rounded-xl flex flex-col items-center justify-center relative transition-all ${isSelected ? 'bg-cobalt text-white' : isToday ? 'bg-cobalt-soft text-cobalt font-bold' : 'hover:bg-paper-deep'}`}>
                  <span className="text-sm font-medium">{day}</span>
                  {eventCount > 0 && !isSelected && (
                    <span className="absolute bottom-1.5 flex gap-0.5">
                      {Array.from({ length: Math.min(eventCount, 3) }).map((_, j) => (
                        <span key={j} className="w-1.5 h-1.5 bg-coral rounded-full" />
                      ))}
                    </span>
                  )}
                  {eventCount > 0 && isSelected && <span className="absolute bottom-1.5 text-[10px] opacity-70">{eventCount} Termine</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tagesdetails */}
        <div className="sl-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="sl-h3">{new Date(selectedDate).toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}</h3>
            <button onClick={() => { setEditingEvent(null); setForm({ title: '', time: '09:00', type: 'lesson', description: '' }); setShowAddDialog(true); }}
              className="p-2 rounded-lg bg-cobalt text-white hover:bg-cobalt-deep transition-colors">
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {dayEvents.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="w-10 h-10 text-ink-faint mx-auto mb-3" />
              <p className="text-ink-soft mb-1">Keine Termine</p>
              <p className="text-sm text-ink-mute">Tippe auf + um einen Termin hinzuzufügen</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dayEvents.map((event) => {
                const Icon = eventIcons[event.type];
                return (
                  <div key={event.id} className={`p-4 rounded-xl border ${eventColors[event.type]} relative group`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="w-4 h-4 opacity-60" />
                          <span className="text-xs font-semibold uppercase tracking-wide opacity-70">{eventLabels[event.type]}</span>
                          <span className="text-sm font-medium">{event.time}</span>
                        </div>
                        <p className="font-semibold truncate">{event.title}</p>
                        {event.description && <p className="text-sm opacity-70 mt-1 line-clamp-2">{event.description}</p>}
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                        <button onClick={() => startEdit(event)} className="p-1.5 rounded-lg hover:bg-white/50"><Edit3 className="w-3.5 h-3.5" /></button>
                        <button onClick={() => handleDelete(event.id)} className="p-1.5 rounded-lg hover:bg-white/50 text-coral"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
